import React from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Typography,
    Button
} from "@material-tailwind/react";
import {
    CogIcon,
    DocumentTextIcon,
    ChartBarIcon,
    InboxIcon
} from "@heroicons/react/24/outline";
import { useNavigate } from 'react-router-dom';

const MessagesIndex = ({ rootUrl, token }) => {
    const navigate = useNavigate();

    const messageModules = [
        {
            title: "Message Rules",
            description: "Configure automatic message routing and car assignment rules",
            icon: CogIcon,
            color: "blue",
            path: "/messages/rules",
            features: ["Rule-based routing", "Priority management", "Hit tracking", "Test functionality"]
        },
        {
            title: "Message Logs",
            description: "View processing history and message routing results",
            icon: DocumentTextIcon,
            color: "green",
            path: "/messages/logs",
            features: ["Processing history", "Rule matches", "Error tracking", "Search & filter"]
        },
        {
            title: "Message Analytics",
            description: "Analyze message patterns and rule performance",
            icon: ChartBarIcon,
            color: "purple",
            path: "/messages/analytics",
            features: ["Performance metrics", "Rule statistics", "Usage trends", "Hit analysis"]
        },
        {
            title: "Message Queue",
            description: "Monitor real-time message processing and queue status",
            icon: InboxIcon,
            color: "orange",
            path: "/messages/queue",
            features: ["Real-time monitoring", "Queue status", "Processing stats", "System health"]
        }
    ];

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-8">
                <Typography variant="h4" color="blue-gray" className="mb-2">
                    Messages Management
                </Typography>
                <Typography color="gray" className="font-normal">
                    Manage message processing rules, monitor routing, and analyze performance
                </Typography>
            </div>

            {/* Module Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {messageModules.map((module, index) => {
                    const IconComponent = module.icon;
                    return (
                        <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                            <CardHeader
                                floated={false}
                                shadow={false}
                                className={`bg-${module.color}-50 rounded-t-xl`}
                            >
                                <div className="flex items-center gap-4 p-4">
                                    <div className={`p-3 bg-${module.color}-500 text-white rounded-lg`}>
                                        <IconComponent className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <Typography variant="h6" color="blue-gray">
                                            {module.title}
                                        </Typography>
                                        <Typography color="gray" className="text-sm">
                                            {module.description}
                                        </Typography>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody className="pt-4">
                                <div className="mb-4">
                                    <Typography variant="small" color="gray" className="font-medium mb-2">
                                        Key Features:
                                    </Typography>
                                    <ul className="space-y-1">
                                        {module.features.map((feature, featureIndex) => (
                                            <li key={featureIndex} className="flex items-center gap-2">
                                                <div className={`w-2 h-2 bg-${module.color}-500 rounded-full`}></div>
                                                <Typography variant="small" color="gray">
                                                    {feature}
                                                </Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <Button
                                    variant="gradient"
                                    color={module.color}
                                    className="w-full"
                                    onClick={() => navigate(module.path)}
                                >
                                    Access {module.title}
                                </Button>
                            </CardBody>
                        </Card>
                    );
                })}
            </div>

            {/* Quick Stats */}
            <div className="mt-8">
                <Card>
                    <CardHeader floated={false} shadow={false} className="rounded-none">
                        <Typography variant="h6" color="blue-gray">
                            Quick Overview
                        </Typography>
                    </CardHeader>
                    <CardBody>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <Typography variant="h4" color="blue">
                                    -
                                </Typography>
                                <Typography color="gray" className="text-sm">
                                    Active Rules
                                </Typography>
                            </div>
                            <div className="text-center">
                                <Typography variant="h4" color="green">
                                    -
                                </Typography>
                                <Typography color="gray" className="text-sm">
                                    Messages Today
                                </Typography>
                            </div>
                            <div className="text-center">
                                <Typography variant="h4" color="purple">
                                    -
                                </Typography>
                                <Typography color="gray" className="text-sm">
                                    Successful Matches
                                </Typography>
                            </div>
                            <div className="text-center">
                                <Typography variant="h4" color="orange">
                                    -
                                </Typography>
                                <Typography color="gray" className="text-sm">
                                    Queue Status
                                </Typography>
                            </div>
                        </div>
                        <Typography variant="small" color="gray" className="text-center mt-4">
                            Real-time statistics will be available once the message processing system is active
                        </Typography>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default MessagesIndex;
